import type { Logger } from "@expert/logging";
import type { Partner, SendExpertAssistMessage } from "@expert/shared-types";
import type { TMessage } from "@simplrsupport/headless-chat-web";

import { getMessageDataType, MESSAGE_DATA_TYPES, useChatSessionStateStore } from "@simplrsupport/headless-chat-web";
import { useMemo } from "react";

import type { CloseGuideSource } from "../types";

import { GuideCard, GuideSelectionCard } from "../components";
import { getGuideCardPropsFromTextMessage, getPropsFromGuideSelectionMessage } from "./logicBuilderResolvers";

export interface GuidedFlowMessageMapperProps {
    callSid?: string;
    clientChannelId?: string;
    latestMessage: TMessage | undefined;
    logger: Logger;
    partner: Partner;
    onGuideClose: (source: CloseGuideSource) => void;
    opened: boolean;
    sendMessage: SendExpertAssistMessage;
    sessionId: string;
    ssoAccessToken: string;
    taskMdn: string;
}

export const GuidedFlowMessageMapper = ({
    callSid,
    clientChannelId,
    latestMessage,
    logger: loggerProp,
    partner,
    onGuideClose,
    opened,
    sendMessage,
    sessionId,
    ssoAccessToken,
    taskMdn,
}: GuidedFlowMessageMapperProps) => {
    const { platformTicketId } = useChatSessionStateStore();

    const logger = useMemo(
        () => loggerProp.child({ module: "GuidedFlowMessageMapper", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    if (!latestMessage) return null;

    const messageType = getMessageDataType(latestMessage) as MESSAGE_DATA_TYPES;
    switch (messageType) {
        case MESSAGE_DATA_TYPES.CUSTOM: {
            const { currentStepId } = getPropsFromGuideSelectionMessage(latestMessage);
            return <GuideSelectionCard logger={logger} stepId={currentStepId} />;
        }
        case MESSAGE_DATA_TYPES.TEXT: {
            const {
                actions,
                alternateText,
                currentStep,
                dropdownInput,
                hasPreviousSteps,
                progress,
                prompt,
                textInput,
            } = getGuideCardPropsFromTextMessage(latestMessage);

            return (
                <GuideCard
                    alternateText={alternateText}
                    actions={actions}
                    callSid={callSid}
                    clientChannelId={clientChannelId}
                    currentStep={currentStep}
                    dropdownInput={dropdownInput}
                    hasPreviousSteps={hasPreviousSteps}
                    logger={logger}
                    onGuideClose={onGuideClose}
                    opened={opened}
                    partner={partner}
                    progress={progress}
                    prompt={prompt}
                    sendMessage={sendMessage}
                    sessionId={sessionId}
                    ssoAccessToken={ssoAccessToken}
                    taskMdn={taskMdn}
                    textInput={textInput}
                />
            );
        }
        default:
            logger.warn({ latestMessage, messageType }, "Solve guide | Unsupported message type.");
            return null;
    }
};
